import React from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { RouteConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
//#endregion
//#region components
import { PageLayouts, RouteBreadCrumb } from "components/common";
import CreateInvoice from "./createInvoice";
import { useLocation } from "react-router-dom";
import { useInvoiceService, useRecurringInvoiceService, useQuotesService, useSelfInvoiceService } from "serviceHooks";
import useInvoicePermissions from "./useInvoicePemissions";
//#endregion

const CreateInvoiceContainer: React.FC = () => {
  const invoiceService = useInvoiceService();
  const selfInvoiceService = useSelfInvoiceService();
  const recurringInvoiceService = useRecurringInvoiceService();
  const quotesService = useQuotesService();

  const [loading, setLoading] = React.useState(true);
  const [templateInvoice, setTemplateInvoice] = React.useState<WintSuperkollAPIModel.Invoice>();
  const [recurringInvoice, setRecurringInvoice] = React.useState<WintSuperkollAPIModel.RecurringInvoice>();
  const [additionalImages, setAdditionalImages] = React.useState<ImageMeta[]>();

  const queryParams = new URLSearchParams(useLocation().search);
  const invoiceId = React.useRef<number>(
    Number(queryParams.get(RouteConstants.QueryParamStrings.InvoiceTemplate) ?? 0)
  );

  const recurringInvoiceId = React.useRef<number>(
    Number(queryParams.get(RouteConstants.QueryParamStrings.Recurring) ?? 0)
  );

  const quotationSerialNumber = React.useRef<number>(
    Number(queryParams.get(RouteConstants.QueryParamStrings.QuotationTemplate) ?? 0)
  );

  const recurringInvoiceTemplateId = React.useRef<number>(
    Number(queryParams.get(RouteConstants.QueryParamStrings.RecurringInvoiceTemplate) ?? 0)
  );

  const selfInvoiceId = React.useRef<string | null>(
    queryParams.get(RouteConstants.QueryParamStrings.SelfInvoiceTemplate)
  );

  const isCreditInvoice = React.useRef<boolean>(
    Boolean(queryParams.get(RouteConstants.QueryParamStrings.IsCredit) ?? false)
  );

  const isRecurringInvoice = React.useRef<boolean>(
    Boolean(
      queryParams.get(RouteConstants.QueryParamStrings.IsRecurring) ??
        queryParams.get(RouteConstants.QueryParamStrings.RecurringInvoiceTemplate) ??
        queryParams.get(RouteConstants.QueryParamStrings.Recurring) ??
        false
    )
  );

  const { canCreateInvoices } = useInvoicePermissions();

  const loadInvoice = (invoiceId: number) => {
    if (isRecurringInvoice.current) {
      invoiceService
        .getRecurringFromInvoice(invoiceId)
        .then((res) => {
          setRecurringInvoice({ ...res, id: 0, status: WintSuperkollAPIModel.RecurringInvoiceState.Created });
        })
        .finally(() => setLoading(false));
    } else {
      (isCreditInvoice.current
        ? invoiceService.getCreditInvoice(invoiceId)
        : invoiceService.getCopiedInvoice(invoiceId)
      )
        .then((res) => {
          setTemplateInvoice(res);
        })
        .finally(() => setLoading(false));
    }
  };

  const loadRecurringInvoice = (invoiceId: number, asTemplate?: boolean) => {
    (asTemplate ? recurringInvoiceService.getCopiedRecurringInvoice(invoiceId) : recurringInvoiceService.get(invoiceId))
      .then((res) => {
        setRecurringInvoice(res);
      })
      .finally(() => setLoading(false));
  };

  const loadInvoiceFromQuotation = (serialNumber: number) => {
    quotesService
      .getInvoiceTemplateFromQuotation(serialNumber)
      .then((res) => {
        setTemplateInvoice(res);
      })
      .finally(() => setLoading(false));
  };

  const loadSelfInvoice = (id: string) => {
    Promise.all([selfInvoiceService.getInvoiceTemplate(id), selfInvoiceService.get(id)])
      .then(([invoice, selfInvoice]) => {
        if (selfInvoice.draftInvoiceId) {
          invoice.id = selfInvoice.draftInvoiceId;
        }

        setTemplateInvoice(invoice);
        setAdditionalImages(
          selfInvoice?.images?.map((meta) => ({
            id: meta.id ?? "",
            contentType: meta.contentType ?? "",
            numberOfPages: 1,
            data: meta.content,
          }))
        );
      })
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    setAdditionalImages(undefined);
    if (recurringInvoiceId.current) {
      loadRecurringInvoice(recurringInvoiceId.current);
    } else if (recurringInvoiceTemplateId.current) {
      loadRecurringInvoice(recurringInvoiceTemplateId.current, true);
    } else if (invoiceId.current) {
      loadInvoice(invoiceId.current);
    } else if (quotationSerialNumber.current) {
      loadInvoiceFromQuotation(quotationSerialNumber.current);
    } else if (selfInvoiceId.current) {
      loadSelfInvoice(selfInvoiceId.current);
    } else {
      setTemplateInvoice(undefined);
      setLoading(false);
    }
  }, [
    invoiceId.current,
    recurringInvoiceId.current,
    recurringInvoiceTemplateId.current,
    isCreditInvoice.current,
    quotationSerialNumber.current,
  ]);

  return (
    <PageLayouts.EntityFullScreen
      id={0}
      entityKey={`invoice`}
      navigation={<RouteBreadCrumb current={TranslationKeys.Invoice.CreateInvoice} showHome />}
      hasPermissions={canCreateInvoices}
      hideSpeedDial
    >
      <CreateInvoice
        invoice={isRecurringInvoice.current ? recurringInvoice : templateInvoice}
        isRecurring={isRecurringInvoice.current}
        loading={loading}
        reloadInvoice={
          recurringInvoiceId.current ? loadRecurringInvoice.bind(null, recurringInvoiceId.current) : undefined
        }
        additionalImages={additionalImages}
      />
    </PageLayouts.EntityFullScreen>
  );
};

export default CreateInvoiceContainer;
